import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import {
  AUTHORIZATION_TEMPLATE_CONSENT_ID,
  IApplePayPaymentResponse,
  ICardDetails,
  ICreatePublicAppointmentPayload,
  IOnlineOrderCustomer as ICustomer,
  IPublicAppointmentConfirmation,
  IParseAddressResponse,
  IProductForScanOrder,
  IProductOnline,
  ONLINE_ORDER_FULLFILMENT_TYPE,
  ONLINE_ORDER_TYPE,
  PAYMENT_TYPE,
  ScanToOrderPayment,
} from "../types/types";
import { EXHUT_DOH_SCAN_ORDER_AUTH_CODE } from "../utils/constants";

export interface IProcessMobilePaymentDto {
  applePaymentData?: IApplePayPaymentResponse;
  type: "APPLE" | "GOOGLE";
}

export function useProcessMobilePayment(storeId?: string, paymentId?: string) {
  return _usePost<any, any, IProcessMobilePaymentDto>({
    path: `public/stores/${storeId}/mobile-payments/${paymentId}`,
    keys: ["public", "stores", storeId!, "mobile-payments", paymentId!],
  });
}

export interface ICreateAdhocMobilePayment {
  type: "APPLE";
  amount: number;
  createdBy: string;
  applePaymentData: IApplePayPaymentResponse;
}
export function useCreateAdhocMobilePayment(storeId?: string) {
  return _usePost<any, any, ICreateAdhocMobilePayment>({
    path: `public/stores/${storeId}/mobile-payments`,
    keys: ["public", "stores", storeId!, "mobile-payments"],
  });
}

export interface IValidateMerchantPayload {
  validationUrl: string;
}
export function useAppleValidateMerchant(storeId?: string) {
  return _usePost<any, any, IValidateMerchantPayload>({
    path: `public/stores/${storeId}/validate-apple-merchant`,
    keys: ["public", "stores", storeId!, "validate-apple-merchant"],
  });
}

export interface ICreateScanToOrderPayload {
  products: IProductForScanOrder[];
  tableCode: number;
  payment?: ScanToOrderPayment;
}
export function useCreateScanToOrder(storeId?: string) {
  return _usePost<any, any, ICreateScanToOrderPayload>({
    path: `public/stores/${storeId}/scan-to-order`,
    keys: ["public", "stores", storeId!, "scan-to-order"],
  });
}

// deprecated. use the ICreateOnlineOrder2
export interface ICreateOnlineOrderPayload {
  products: IProductForScanOrder[];
  customer: ICustomer;
  amount: number;
  total: number;
  tax: number;
  type: "BATCH_DELIVERY"; // Later support "PICKUP" | "DELIVERY" |
}

// deprecated - use useCreateOnlineOrder2 instead
export function useCreateOnlineOrder(storeId?: string) {
  return _usePost<any, any, ICreateOnlineOrderPayload>({
    path: `public/stores/${storeId}/online-order`,
    keys: ["public", "stores", storeId!, "scan-to-order"],
  });
}

export interface ICreateOnlineOrder2Payload {
  products: IProductOnline[];
  customer: ICustomer;
  amount: number;
  total: number;
  tax: number;
  taxAmount?: number; // GST orders send the computed tax; sale level tax stays 0.
  type: ONLINE_ORDER_TYPE; // Later support "PICKUP" | "DELIVERY" |
  paymentType: PAYMENT_TYPE;
  nonce: string;
  zipCode?: string; // for card payments.
  authConsentTemplateId: AUTHORIZATION_TEMPLATE_CONSENT_ID;
  cardDetails?: ICardDetails;
  fullfilmentType: ONLINE_ORDER_FULLFILMENT_TYPE;
}

export function useCreateOnlineOrder2(storeId?: string) {
  return _usePost<any, any, ICreateOnlineOrder2Payload>({
    path: `public/stores/${storeId}/online-orders`,
    keys: ["public", "stores", storeId!, "online-orders"],
  });
}

export function useCreatePublicAppointment(storeId?: string) {
  return _usePost<
    IPublicAppointmentConfirmation,
    any,
    ICreatePublicAppointmentPayload
  >({
    path: `public/stores/${storeId}/scheduling/appointments`,
    keys: ["public", "stores", storeId!, "scheduling", "appointments"],
  });
}

export interface IParseAddressPayload {
  address: string;
}

export function useParseAddress(storeId?: string) {
  return _usePost<IParseAddressResponse, any, IParseAddressPayload>({
    path: `public/stores/${storeId}/online-order/parse-address`,
    keys: ["public", "stores", storeId!, "online-order", "parse-address"],
  });
}

export interface IValidateCheckoutReadyPayload {
  tableCode?: number;
}

export function useValidateCheckoutReadyForScanOrder(storeId?: string) {
  return _usePost<any, any, IValidateCheckoutReadyPayload>({
    path: `public/stores/${storeId}/scan-to-order/validate-checkout-ready`,
    keys: ["public", "stores", storeId!, "scan-to-order", "validate-checkout"],
  });
}

export function _usePost<T1, T2, T3>({
  path,
  keys,
  resetKeys,
  authenticated = true,
  host,
  replacePathParams,
}: {
  path: string;
  keys?: string[];
  resetKeys?: string[];
  authenticated?: boolean;
  host?: string;
  replacePathParams?: string;
}) {
  let _url = host
    ? `${host}/${[path]}`
    : `${process.env.REACT_APP_BE_URL}/${path}`;

  let headers: any = {
    "Content-Type": "application/json",
  };
  if (authenticated) {
    headers["x-scan-to-order-auth-code"] = localStorage.getItem(
      EXHUT_DOH_SCAN_ORDER_AUTH_CODE,
    );
  } else {
    headers["Access-Type"] = "offline";
  }

  const queryClient = useQueryClient();
  return useMutation<T1, T2, T3>({
    mutationFn: async (req) => {
      if (replacePathParams) {
        _url = _url.replace(
          `:${replacePathParams}`,
          (req as any)[replacePathParams],
        );
      }
      return axios
        .post(_url, req, {
          headers,
        })
        .then((resp) => resp.data);
    },
    mutationKey: keys,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: resetKeys }),
  });
}
